import { defineComponent as _defineComponent } from 'vue'
import {
    setWilcomCredentials,
    setMelcoCredentials,
    setOpenAiApiKey,
    setRemoveBgApiKey,
    setVanceAiApiKey,
    setCutoutProApiKey,
    setSpotifyCredentials,
    deleteCanvaCredentials,
    setGoogleGeminiApiKey,
    setOpenRouterApiKey,
    setPicWishApiKey,
    getRateLimits,
    type RateLimitDto
} from '@/api/user';
import { CustomilyUser, Roles } from '@/typings/user';
import LoaderBtn from '@/components/LoaderBtn/LoaderBtn.vue';
import ApiKeyInput from '@/components/ApiKeyInput/ApiKeyInput.vue';
import ApiRateLimitModal from '@/components/Integrations/ApiRateLimitModal.vue';
import store from '@/store';
import { computed, onBeforeMount, reactive, ref } from 'vue';
import { AxiosError } from 'axios';


export default /*#__PURE__*/_defineComponent({
  __name: 'Integrations',
  emits: ["user-updated"],
  setup(__props, { emit }: { emit: ({
    (event: 'user-updated'): void;
}), expose: any, slots: any, attrs: any }) {



const isImageEffectsEnabled = process.env.VUE_APP_IMAGE_EFFECTS_ENABLED === 'true';

const removeBgApiKey = ref('');
const wilcomAppId = ref('');
const wilcomAppKey = ref('');
const melcoUsername = ref('');
const melcoApiKey = ref('');
const cutoutProApiKey = ref('');
const cutoutProError = ref('');
const googleGeminiError = ref('');
const openRouterError = ref('');

const vanceAiApiKey = ref('');
const openAiApiKey = ref('');
const openAiApiKeyState = ref<boolean | null>(null);
const openAiError = ref('');

const spotifyClientID = ref('');
const spotifyClientSecret = ref('');

const googleGeminiApiKey = ref('');
const openRouterApiKey = ref('');

const picWishApiKey = ref('');
const picWishError = ref('');

const rateLimits = ref<Record<string, RateLimitDto>>({});
const showRateLimitModal = ref(false);
const activeRateLimit = ref<RateLimitDto & { label: string }>({
    provider: '',
    label: '',
    attemptsPerIp: null,
    retryAfterMinutes: null
});

const openRateLimitModal = (provider: string, label: string) => {
    const existing = rateLimits.value[provider];
    activeRateLimit.value = {
        provider,
        label,
        attemptsPerIp: existing?.attemptsPerIp ?? null,
        retryAfterMinutes: existing?.retryAfterMinutes ?? null
    };
    showRateLimitModal.value = true;
};

const onRateLimitSaved = async () => {
    try {
        const list = await getRateLimits();
        rateLimits.value = Object.fromEntries(list.map((r) => [r.provider, r]));
    } catch {
        // ignore — modal already closed, stale values are acceptable
    }
};

const showFields = reactive({
    removeBg: false,
    vanceAi: false,
    openAi: false,
    spotifyClientId: false,
    spotifyClientSecret: false,
    cutoutPro: false,
    wilcomAppId: false,
    wilcomAppKey: false,
    melcoUsername: false,
    melcoApiKey: false,
    googleGemini: false,
    openRouter: false,
    picWish: false
});

const savingState = reactive({
    vanceAi: false,
    removeBg: false,
    wilcomCredentials: false,
    melcoCredentials: false,
    openAi: false,
    cutoutPro: false,
    spotifyCredentials: false,
    canvaCredentials: false,
    googleGemini: false,
    openRouter: false,
    picWish: false
});

const user = computed((): CustomilyUser => store.state.user!);
const isAdmin = computed(() => user.value?.AccessRoles?.Serialized?.split(',').includes(Roles.ADMIN) ?? false);
const openAiVerified = computed(() => {
    if (!openAiApiKey.value) {
        return true;
    }
    return (
        typeof openAiApiKey.value === 'string' && openAiApiKey.value.length > 0 && openAiApiKey.value.startsWith('sk')
    );
});

const modifiedRemoveBgApiKey = computed(() => user.value.RemoveBgAPIKey != removeBgApiKey.value);
const modifiedCutoutProApiKey = computed(() => user.value.CutoutproAPIKey != cutoutProApiKey.value);

onBeforeMount(async () => {
    removeBgApiKey.value = user.value.RemoveBgAPIKey || '';
    vanceAiApiKey.value = user.value.VanceAiAPIKey || '';
    openAiApiKey.value = user.value.OpenAiAPIKey || '';
    melcoUsername.value = user.value.MelcoUsername || '';
    melcoApiKey.value = user.value.MelcoApiKey || '';
    wilcomAppId.value = user.value.WilcomAppId || '';
    wilcomAppKey.value = user.value.WilcomAppKey || '';
    cutoutProApiKey.value = user.value.CutoutproAPIKey || '';
    spotifyClientID.value = user.value.SpotifyClientID || '';
    spotifyClientSecret.value = user.value.SpotifyClientSecret || '';
    googleGeminiApiKey.value = user.value.GoogleGeminiAPIKey || '';
    openRouterApiKey.value = user.value.OpenRouterAPIKey || '';
    picWishApiKey.value = user.value.PicWishAPIKey || '';

    try {
        const list = await getRateLimits();
        rateLimits.value = Object.fromEntries(list.map((r) => [r.provider, r]));
    } catch {
        // non-critical — rate limit buttons still open the modal, just without pre-filled values
    }
});

const onRemoveBgSubmit = async () => {
    try {
        savingState.removeBg = true;
        await setRemoveBgApiKey(removeBgApiKey.value);
    } finally {
        savingState.removeBg = false;
        emit('user-updated');
    }
};

const onVanceAiSubmit = async () => {
    savingState.vanceAi = true;
    await setVanceAiApiKey(vanceAiApiKey.value);
    savingState.vanceAi = false;
    emit('user-updated');
};

const onOpenAiSubmit = async () => {
    savingState.openAi = true;
    try {
        await setOpenAiApiKey(openAiApiKey.value);
        openAiApiKeyState.value = true;
        openAiError.value = '';
        emit('user-updated');
    } catch (error) {
        openAiApiKeyState.value = false;
        openAiError.value = (error as Error).message;
    } finally {
        savingState.openAi = false;
    }
};

const onWilcomCredentialsSubmit = async () => {
    try {
        savingState.wilcomCredentials = true;
        await setWilcomCredentials(wilcomAppId.value, wilcomAppKey.value);
    } finally {
        savingState.wilcomCredentials = false;
        emit('user-updated');
    }
};

const onMelcoCredentialsSubmit = async () => {
    try {
        savingState.melcoCredentials = true;
        await setMelcoCredentials(melcoUsername.value, melcoApiKey.value);
    } finally {
        savingState.melcoCredentials = false;
        emit('user-updated');
    }
};

const onCutoutProSubmit = async () => {
    try {
        savingState.cutoutPro = true;
        cutoutProError.value = '';
        await setCutoutProApiKey(cutoutProApiKey.value);
        emit('user-updated');
    } catch (error) {
        cutoutProError.value = (error as AxiosError).response?.data?.Message;
    } finally {
        savingState.cutoutPro = false;
    }
};

const onSpotifyCredentialsSubmit = async () => {
    try {
        savingState.spotifyCredentials = true;
        await setSpotifyCredentials(spotifyClientID.value, spotifyClientSecret.value);
    } finally {
        savingState.spotifyCredentials = false;
        emit('user-updated');
    }
};

const diconnectUserFromCanva = async () => {
    try {
        savingState.canvaCredentials = true;
        await deleteCanvaCredentials();
    } finally {
        savingState.canvaCredentials = false;
        emit('user-updated');
    }
};

const onGoogleGeminiApiSubmit = async () => {
    try {
        savingState.googleGemini = true;
        googleGeminiError.value = '';
        await setGoogleGeminiApiKey(googleGeminiApiKey.value);
    } catch (error) {
        googleGeminiError.value = (error as Error).message;
    } finally {
        savingState.googleGemini = false;
        emit('user-updated');
    }
};

const onOpenRouterApiSubmit = async () => {
    try {
        savingState.openRouter = true;
        openRouterError.value = '';
        await setOpenRouterApiKey(openRouterApiKey.value);
    } catch (error) {
        openRouterError.value = (error as Error).message;
    } finally {
        savingState.openRouter = false;
        emit('user-updated');
    }
};
const onPicWishApiSubmit = async () => {
    try {
        savingState.picWish = true;
        picWishError.value = '';
        await setPicWishApiKey(picWishApiKey.value);
    } catch (error) {
        picWishError.value = (error as Error).message;
    } finally {
        savingState.picWish = false;
        emit('user-updated');
    }
};

return { __sfc: true,emit, isImageEffectsEnabled, removeBgApiKey, wilcomAppId, wilcomAppKey, melcoUsername, melcoApiKey, cutoutProApiKey, cutoutProError, googleGeminiError, openRouterError, vanceAiApiKey, openAiApiKey, openAiApiKeyState, openAiError, spotifyClientID, spotifyClientSecret, googleGeminiApiKey, openRouterApiKey, picWishApiKey, picWishError, rateLimits, showRateLimitModal, activeRateLimit, openRateLimitModal, onRateLimitSaved, showFields, savingState, user, isAdmin, openAiVerified, modifiedRemoveBgApiKey, modifiedCutoutProApiKey, onRemoveBgSubmit, onVanceAiSubmit, onOpenAiSubmit, onWilcomCredentialsSubmit, onMelcoCredentialsSubmit, onCutoutProSubmit, onSpotifyCredentialsSubmit, diconnectUserFromCanva, onGoogleGeminiApiSubmit, onOpenRouterApiSubmit, onPicWishApiSubmit, LoaderBtn, ApiKeyInput, ApiRateLimitModal }
}

})